"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebformRenderer = void 0;
const react_1 = __importStar(require("react"));
const WebformSection_1 = require("./WebformSection");
const FamilySection_1 = require("./sections/FamilySection");
const AddressSection_1 = require("./sections/AddressSection");
const EducationSection_1 = require("./sections/EducationSection");
const WorkExperienceSection_1 = require("./sections/WorkExperienceSection");
const DocumentsSection_1 = require("./sections/DocumentsSection");
const TestRegistrationSection_1 = require("./sections/TestRegistrationSection");
const PaymentSection_1 = require("./sections/PaymentSection");
const validation_1 = require("./validation");
/* ── Section icon + color mapping ── */
const SECTION_ICONS = {
    basic: { icon: '\u{1F464}', color: 'blue' },
    program: { icon: '\u{1F4D6}', color: 'purple' },
    family: { icon: '\u{1F465}', color: 'teal' },
    address: { icon: '\u{1F4CD}', color: 'orange' },
    education: { icon: '\u{1F393}', color: 'blue' },
    workExperience: { icon: '\u{1F4BC}', color: 'purple' },
    documents: { icon: '\u{1F4CE}', color: 'teal' },
    photo: { icon: '\u{1F4F7}', color: 'orange' },
    testRegistration: { icon: '\u{1F4DD}', color: 'blue' },
    payment: { icon: '\u{1F4B3}', color: 'purple' },
    extras: { icon: '\u{2728}', color: 'teal' },
};
/**
 * WebformRenderer — UCAS-inspired shared form component.
 *
 * Takes a JSON form definition + lookups and renders a polished multi-section form.
 * Used in both ilearn (student/public) and iteach (staff).
 */
const WebformRenderer = ({ definition, lookups, initialData, onSubmit, onSaveDraft, readOnly = false, mode = 'create', }) => {
    // Progressive mode: step 1 captures draft fields, step 2 shows full form
    const draftConfig = definition.config?.saveDraft;
    const isProgressive = !!(draftConfig?.enforceDraftOnStart && draftConfig?.fields && onSaveDraft);
    const hasExistingEnquiry = !!(initialData?.enquiryId);
    const [formData, setFormData] = (0, react_1.useState)(initialData || {});
    const [errors, setErrors] = (0, react_1.useState)({});
    const [submitting, setSubmitting] = (0, react_1.useState)(false);
    const [submitError, setSubmitError] = (0, react_1.useState)(null);
    const [submitSuccess, setSubmitSuccess] = (0, react_1.useState)(null);
    const [step, setStep] = (0, react_1.useState)(isProgressive && !hasExistingEnquiry ? 1 : 2);
    const handleChange = (0, react_1.useCallback)((key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => {
            if (!prev[key])
                return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);
    // Step 1 → Step 2 transition: save draft, get enquiryId, advance
    const handleDraftContinue = (0, react_1.useCallback)(async (e) => {
        e.preventDefault();
        if (!onSaveDraft || !draftConfig?.fields)
            return;
        // Validate draft fields
        const draftErrors = {};
        const fieldLabels = {
            email: 'Email', mobile: 'Mobile', firstName: 'Name', dob: 'Date of Birth',
        };
        for (const [key, level] of Object.entries(draftConfig.fields)) {
            if (level >= 2 && !formData[key]) {
                draftErrors[key] = `${fieldLabels[key] || key} is required`;
            }
        }
        if (Object.keys(draftErrors).length > 0) {
            setErrors(draftErrors);
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await onSaveDraft(formData);
            if (result?.enquiryId) {
                setFormData((prev) => ({ ...prev, enquiryId: result.enquiryId }));
            }
            setStep(2);
        }
        catch (err) {
            setSubmitError(err?.message || 'Failed to save draft. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    }, [onSaveDraft, draftConfig, formData]);
    const handleSubmit = (0, react_1.useCallback)(async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(null);
        const validationErrors = (0, validation_1.validateForm)(definition, formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorKey = Object.keys(validationErrors)[0];
            const el = document.querySelector(`[name="${firstErrorKey}"], [id*="${firstErrorKey}"]`);
            if (el)
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setSubmitting(true);
        try {
            await onSubmit(formData);
            const msg = definition.confirmationMessage?.success || 'Form submitted successfully.';
            setSubmitSuccess(msg);
        }
        catch (err) {
            setSubmitError(err?.message || 'An error occurred while submitting the form.');
        }
        finally {
            setSubmitting(false);
        }
    }, [definition, formData, onSubmit]);
    const handleSaveDraft = (0, react_1.useCallback)(async () => {
        if (!onSaveDraft)
            return;
        setSubmitting(true);
        try {
            await onSaveDraft(formData);
        }
        finally {
            setSubmitting(false);
        }
    }, [formData, onSaveDraft]);
    // Success view
    if (submitSuccess) {
        return (react_1.default.createElement("div", { className: "wf" },
            react_1.default.createElement("div", { className: "success-message" },
                react_1.default.createElement("div", { className: "success-icon" }, '\u{2705}'),
                react_1.default.createElement("div", { className: "success-title" }, "Application Submitted"),
                react_1.default.createElement("div", { className: "success-text" }, submitSuccess))));
    }
    // Step 1: Draft capture form
    if (step === 1 && isProgressive) {
        const draftFields = draftConfig.fields;
        const fieldDefs = {
            firstName: { label: "Full Name", type: "text" },
            email: { label: "Email Address", type: "email" },
            mobile: { label: "Mobile Number", type: "phone" },
            dob: { label: "Date of Birth", type: "date" },
        };
        return (react_1.default.createElement("div", { className: "wf" },
            react_1.default.createElement("div", { className: "section-card" },
                react_1.default.createElement("div", { className: "section-header" },
                    react_1.default.createElement("div", { className: "section-icon blue" }, '\u{270F}\u{FE0F}'),
                    react_1.default.createElement("div", null,
                        react_1.default.createElement("div", { className: "section-title" }, "Let's get started"),
                        react_1.default.createElement("div", { className: "section-subtitle" }, "Please provide your basic details to begin your application."))),
                react_1.default.createElement("div", { className: "section-body" },
                    react_1.default.createElement("form", { onSubmit: handleDraftContinue, noValidate: true },
                        react_1.default.createElement("div", { className: "form-row" }, Object.entries(draftFields)
                            .filter(([, level]) => level > 0)
                            .map(([key, level]) => {
                            const fd = fieldDefs[key] || { label: key, type: 'text' };
                            const isReq = level >= 2;
                            return (react_1.default.createElement("div", { key: key, className: "form-group" },
                                react_1.default.createElement("label", { className: "form-label" },
                                    fd.label,
                                    isReq && react_1.default.createElement("span", { className: "required-star" }, "*")),
                                react_1.default.createElement("input", { type: fd.type === 'email' ? 'email' : fd.type === 'date' ? 'date' : fd.type === 'phone' ? 'tel' : 'text', className: `form-input${errors[key] ? ' error' : ''}`, value: formData[key] || '', onChange: (e) => handleChange(key, e.target.value) }),
                                errors[key] && react_1.default.createElement("div", { className: "field-error" }, errors[key])));
                        })),
                        submitError && react_1.default.createElement("div", { className: "error-alert" }, submitError),
                        react_1.default.createElement("div", { className: "wf-btn-row" },
                            react_1.default.createElement("div", null),
                            react_1.default.createElement("button", { type: "submit", className: "wf-btn wf-btn-primary", disabled: submitting }, submitting ? 'Saving...' : 'Continue')))))));
    }
    return (react_1.default.createElement("div", { className: `wf${readOnly ? ' wf-view-mode' : ''}` },
        definition.headerHtml && (react_1.default.createElement("div", { className: "wf-header-html", dangerouslySetInnerHTML: { __html: definition.headerHtml } })),
        definition.topSection?.topSection && (react_1.default.createElement("div", { dangerouslySetInnerHTML: { __html: definition.topSection.topSection }, style: { marginBottom: 16 } })),
        !readOnly && (react_1.default.createElement("div", { className: "info-banner" },
            react_1.default.createElement("span", { className: "info-banner-icon" }, '\u{1F4A1}'),
            react_1.default.createElement("span", { className: "info-banner-text" },
                "Fields marked with ",
                react_1.default.createElement("span", { style: { color: '#FF6B35', fontWeight: 600 } }, "*"),
                " are required.",
                definition.config?.saveDraft?.enabled && ' Your progress can be saved as a draft.'))),
        react_1.default.createElement("form", { onSubmit: handleSubmit, noValidate: true },
            definition.sections.map((section) => {
                const iconInfo = SECTION_ICONS[section.id] || { icon: '\u{1F4CB}', color: 'blue' };
                return (react_1.default.createElement("div", { className: "section-card", id: `section-${section.id}`, key: section.id },
                    react_1.default.createElement("div", { className: "section-header" },
                        react_1.default.createElement("div", { className: `section-icon ${iconInfo.color}` }, iconInfo.icon),
                        react_1.default.createElement("div", null,
                            react_1.default.createElement("div", { className: "section-title" }, section.title),
                            section.description && react_1.default.createElement("div", { className: "section-subtitle" }, section.description))),
                    react_1.default.createElement("div", { className: "section-body" }, renderSectionContent(section, formData, errors, handleChange, readOnly, lookups))));
            }),
            definition.termsAndConditions && (react_1.default.createElement("div", { className: "terms-section" },
                react_1.default.createElement("label", null,
                    react_1.default.createElement("input", { type: "checkbox", checked: !!formData.termsAccepted, onChange: (e) => handleChange('termsAccepted', e.target.checked), disabled: readOnly }),
                    definition.termsAndConditions),
                errors.termsAccepted && (react_1.default.createElement("div", { className: "terms-error" }, errors.termsAccepted)))),
            submitError && (react_1.default.createElement("div", { className: "error-alert" }, submitError)),
            !readOnly && (react_1.default.createElement("div", { className: "wf-btn-row" },
                onSaveDraft && definition.config?.saveDraft?.enabled ? (react_1.default.createElement("button", { type: "button", className: "wf-btn wf-btn-save-draft", onClick: handleSaveDraft, disabled: submitting }, "Save Draft")) : react_1.default.createElement("div", null),
                react_1.default.createElement("div", { style: { display: 'flex', gap: 12 } },
                    react_1.default.createElement("button", { type: "submit", className: "wf-btn wf-btn-primary", disabled: submitting }, submitting ? 'Submitting...' : mode === 'edit' ? 'Update' : 'Submit Application'))))),
        definition.footerHtml && (react_1.default.createElement("div", { dangerouslySetInnerHTML: { __html: definition.footerHtml } }))));
};
exports.WebformRenderer = WebformRenderer;
/**
 * Routes a section to its specialized renderer based on section.type.
 * These renderers only produce the inner content (no card wrapper).
 */
function renderSectionContent(section, data, errors, onChange, disabled, lookups) {
    const commonProps = { data, errors, onChange, disabled, lookups };
    switch (section.type) {
        case 'family':
            return react_1.default.createElement(FamilySection_1.FamilySection, { section: section, ...commonProps });
        case 'address':
            return react_1.default.createElement(AddressSection_1.AddressSection, { section: section, ...commonProps });
        case 'education':
            return react_1.default.createElement(EducationSection_1.EducationSection, { section: section, ...commonProps });
        case 'workExperience':
            return react_1.default.createElement(WorkExperienceSection_1.WorkExperienceSection, { section: section, ...commonProps });
        case 'documents':
            return react_1.default.createElement(DocumentsSection_1.DocumentsSection, { section: section, ...commonProps });
        case 'testRegistration':
            return react_1.default.createElement(TestRegistrationSection_1.TestRegistrationSection, { section: section, ...commonProps });
        case 'payment':
            return react_1.default.createElement(PaymentSection_1.PaymentSection, { section: section, ...commonProps });
        default:
            return react_1.default.createElement(WebformSection_1.WebformSection, { section: section, ...commonProps });
    }
}
